import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';

/**
 * OptOutPromo - Handles opting out of promotional SMS messages
 */
export default function OptOutPromo() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const mobile = searchParams.get('mobile');

  useEffect(() => {
    const optOut = async () => {
      if (!mobile) {
        console.error('No mobile number provided');
        setStatus('error');
        return;
      }

      try {
        const response = await fetch(`/api/opt-out-promo-sms?mobile=${encodeURIComponent(mobile)}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        });

        const data = await response.json();
        console.log('Opt-out response:', data);

        if (response.ok && data.success !== false) {
          setStatus('success');
        } else {
          console.error('Opt-out failed:', data);
          setStatus('error');
        }
      } catch (error) {
        console.error('Opt-out error:', error);
        setStatus('error');
      }
    };

    optOut();
  }, [mobile]);

  // Auto-redirect to home after 3 seconds on success
  useEffect(() => {
    if (status === 'success') {
      const timer = setTimeout(() => {
        navigate('/', { replace: true });
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [status, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {status === 'processing' && (
          <>
            <div className="flex justify-center mb-6">
              <Loader2 className="h-16 w-16 text-brand-pink animate-spin" />
            </div>
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">
              Processing Your Request
            </h2>
            <p className="text-gray-600">
              Please wait while we opt you out of promotional SMS messages...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="flex justify-center mb-6">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <h2 className="text-2xl font-semibold mb-4 text-green-700">
              Successfully Opted Out
            </h2>
            <p className="text-gray-600 mb-2">
              You have successfully opted out of promotional SMS messages.
            </p>
            <p className="text-gray-600 mb-4">
              You will no longer receive marketing texts from us.
            </p>
            <p className="text-sm text-gray-500">
              Redirecting you to home in 3 seconds...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex justify-center mb-6">
              <XCircle className="h-16 w-16 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold mb-2 text-red-600">
              Opt-Out Failed
            </h2>
            <p className="text-gray-600 mb-6">
              {!mobile 
                ? "No mobile number was provided in the link."
                : "There was an issue processing your opt-out request. Please try again later or contact support."}
            </p>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="w-full bg-brand-pink text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#e391c4] transition-colors"
            >
              Go to Home
            </button>
          </>
        )}
      </div>
    </div>
  );
}

